/**
 * Tab "Memory" dentro de UserDetail (fase 26.D).
 *
 * Sub-tabs:
 *   - Facts: memory_facts (long-term) — disable/enable/delete con MutationModal.
 *   - Episodic: episodic_events (short-term, 30d TTL) — disable/delete.
 *   - Consolidations: timeline de las corridas del job memory.consolidate, con
 *     botón "Consolidate now" para encolar manual (trigger='admin').
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useUrlParam, useUrlTab } from '../../lib/useUrlTab.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteEpisodicEvent,
  deleteMemoryFact,
  disableEpisodicEvent,
  disableMemoryFact,
  enableMemoryFact,
  enqueueMemoryConsolidate,
  fetchUserMemoryConsolidations,
  fetchUserMemoryEpisodic,
  fetchUserMemoryFacts,
  type EpisodicItem,
  type MemoryFactItem,
} from '../../api/admin.js';
import { TabsBar, type TabsBarTab } from '../../components/ui/TabsBar.js';
import { MutationModal } from '../../components/ui/MutationModal.js';
import { formatDateTime } from '../../lib/format.js';
import { GATED_CONTENT_PLACEHOLDER } from '../../lib/privacy.js';

type SubTab = 'facts' | 'episodic' | 'consolidations';
const SUB_TABS: ReadonlyArray<TabsBarTab<SubTab>> = [
  { id: 'facts', label: 'Facts' },
  { id: 'episodic', label: 'Episodic' },
  { id: 'consolidations', label: 'Consolidations' },
];

export interface MemoryTabProps {
  userId: string;
}

export function MemoryTab({ userId }: MemoryTabProps): ReactElement {
  const [sub, setSub] = useUrlTab<SubTab>('msub', SUB_TABS, 'facts');
  return (
    <section className="card" style={{ marginTop: 24 }}>
      <header className="card-head">
        <h3 className="card-title">Memoria</h3>
      </header>
      <div className="pad-6">
        <TabsBar tabs={SUB_TABS} active={sub} onChange={setSub} />
        {sub === 'facts' ? <FactsTab userId={userId} /> : null}
        {sub === 'episodic' ? <EpisodicTab userId={userId} /> : null}
        {sub === 'consolidations' ? <ConsolidationsTab userId={userId} /> : null}
      </div>
    </section>
  );
}

// ─── Facts ────────────────────────────────────────────────────────────────

function FactsTab({ userId }: { userId: string }): ReactElement {
  // Prefijo `m` para no chocar con otros params del page (`tab`, `msub`).
  const [disParam, setDisParam] = useUrlParam('mdis');
  const includeDisabled = disParam === '1';
  const [scopeParam, setScopeParam] = useUrlParam('mscope');
  const scope = (scopeParam as '' | MemoryFactItem['scope'] | null) ?? '';
  const [kindParam, setKindParam] = useUrlParam('mkind');
  const kind = kindParam ?? '';
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'user-memory-facts', userId, { includeDisabled, scope, kind }],
    queryFn: () =>
      fetchUserMemoryFacts(userId, {
        include_disabled: includeDisabled,
        ...(scope ? { scope } : {}),
        ...(kind ? { kind } : {}),
        limit: 50,
      }),
  });

  const [confirm, setConfirm] = useState<
    { mode: 'disable' | 'delete' | 'enable'; fact: MemoryFactItem } | null
  >(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'user-memory-facts', userId] });
  };

  const disableMu = useMutation({
    mutationFn: (input: { factId: string; reason: string }) =>
      disableMemoryFact(userId, input.factId, input.reason),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const enableMu = useMutation({
    mutationFn: (input: { factId: string }) => enableMemoryFact(userId, input.factId),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const deleteMu = useMutation({
    mutationFn: (input: { factId: string; reason: string }) =>
      deleteMemoryFact(userId, input.factId, input.reason),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  return (
    <>
      <div className="filter-row">
        <select
          value={scope}
          onChange={(e) => setScopeParam(e.target.value || null)}
        >
          <option value="">Todos los scopes</option>
          <option value="profile">profile</option>
          <option value="module_shared">module_shared</option>
          <option value="module_private">module_private</option>
        </select>
        <input
          placeholder="kind (ej. preference)"
          value={kind}
          onChange={(e) => setKindParam(e.target.value || null)}
        />
        <label>
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setDisParam(e.target.checked ? '1' : null)}
          />
          Incluir desactivados
        </label>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando facts…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : query.data.items.length === 0 ? (
        <div className="loading">Sin facts para este filtro.</div>
      ) : (
        <table className="table" data-testid="memory-facts-table" style={{ marginTop: 'var(--s-4)' }}>
          <thead>
            <tr>
              <th>scope</th>
              <th>kind</th>
              <th>body</th>
              <th>origin</th>
              <th>updated</th>
              <th>status</th>
              <th>acciones</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((f) => (
              <tr key={f.id} data-testid={`memory-fact-row-${f.id}`}>
                <td className="mono tt-tert">{f.scope}</td>
                <td className="mono">{f.kind}</td>
                {/* Fact gateado (módulo sensible): el backend manda body=null +
                    gated=true. Metadata visible; el contenido, nunca. */}
                {f.gated || f.body === null ? (
                  <td className="tt-tert" data-testid={`memory-fact-gated-${f.id}`}>
                    <em>🔒 {GATED_CONTENT_PLACEHOLDER}</em>
                  </td>
                ) : (
                  <td title={f.indexText ? `${f.body}\n\nEN (search): ${f.indexText}` : f.body}>
                    {truncate(f.body, 80)}
                    {f.indexText ? (
                      <div className="tt-tert" data-testid={`memory-fact-index-${f.id}`}>
                        EN: {truncate(f.indexText, 80)}
                      </div>
                    ) : null}
                  </td>
                )}
                <td className="mono tt-tert">{f.originModule}</td>
                <td className="tt-tert">{formatDateTime(f.updatedAt)}</td>
                <td>
                  {f.disabledAt ? (
                    <span className="badge b-neutral" title={f.disabledReason ?? ''}>
                      disabled
                    </span>
                  ) : (
                    <span className="badge b-info">active</span>
                  )}
                </td>
                <td>
                  {f.disabledAt ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setConfirm({ mode: 'enable', fact: f })}
                    >
                      Enable
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setConfirm({ mode: 'disable', fact: f })}
                      data-testid={`disable-${f.id}`}
                    >
                      Disable
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => setConfirm({ mode: 'delete', fact: f })}
                    style={{ marginLeft: 8 }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirm?.mode === 'disable' ? (
        <MutationModal
          open
          title="Desactivar fact"
          description={
            <>
              El fact dejará de aparecer en el system prompt de las próximas sesiones. Es
              reversible — podés re-activarlo después.
            </>
          }
          confirmLabel="Desactivar"
          tone="warn"
          requiresReason
          isPending={disableMu.isPending}
          errorMessage={disableMu.error?.message ?? null}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => disableMu.mutate({ factId: confirm.fact.id, reason })}
        />
      ) : null}
      {confirm?.mode === 'enable' ? (
        <MutationModal
          open
          title="Re-activar fact"
          confirmLabel="Re-activar"
          isPending={enableMu.isPending}
          errorMessage={enableMu.error?.message ?? null}
          onCancel={() => setConfirm(null)}
          onConfirm={() => enableMu.mutate({ factId: confirm.fact.id })}
        />
      ) : null}
      {confirm?.mode === 'delete' ? (
        <MutationModal
          open
          title="Eliminar fact"
          description="Hard delete. La operación es irreversible — el fact se borra de la DB."
          confirmLabel="Eliminar"
          tone="danger"
          requiresReason
          requiresLiteralConfirm="DELETE"
          isPending={deleteMu.isPending}
          errorMessage={deleteMu.error?.message ?? null}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => deleteMu.mutate({ factId: confirm.fact.id, reason })}
        />
      ) : null}
    </>
  );
}

// ─── Episodic ─────────────────────────────────────────────────────────────

function EpisodicTab({ userId }: { userId: string }): ReactElement {
  // `mdis`/`mmod` para que F5 vuelva al mismo filtro del tab Episodic.
  const [disParam, setDisParam] = useUrlParam('mdis');
  const includeDisabled = disParam === '1';
  const [modParam, setModParam] = useUrlParam('mmod');
  const moduleSlug = modParam ?? 'english';
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'user-memory-episodic', userId, { includeDisabled, moduleSlug }],
    queryFn: () =>
      fetchUserMemoryEpisodic(userId, {
        module: moduleSlug,
        include_disabled: includeDisabled,
        limit: 50,
      }),
  });

  const [confirm, setConfirm] = useState<
    { mode: 'disable' | 'delete'; event: EpisodicItem } | null
  >(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'user-memory-episodic', userId] });
  };

  const disableMu = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      disableEpisodicEvent(userId, input.id, input.reason),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const deleteMu = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      deleteEpisodicEvent(userId, input.id, input.reason),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  return (
    <>
      <div className="filter-row">
        <select
          value={moduleSlug}
          onChange={(e) => setModParam(e.target.value === 'english' ? null : e.target.value)}
        >
          <option value="english">english</option>
          <option value="coach">coach</option>
          <option value="fonix">fonix</option>
        </select>
        <label>
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setDisParam(e.target.checked ? '1' : null)}
          />
          Incluir desactivados
        </label>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando events…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : query.data.items.length === 0 ? (
        <div className="loading">
          Sin episodic events. Los openers se distillan al cerrar cada sesión voice.
        </div>
      ) : (
        <table className="table" style={{ marginTop: 'var(--s-4)' }}>
          <thead>
            <tr>
              <th>kind</th>
              <th>body</th>
              <th>recall</th>
              <th>occurred</th>
              <th>expires</th>
              <th>status</th>
              <th>acciones</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((e) => (
              <tr key={e.id}>
                <td className="mono">{e.kind}</td>
                <td title={e.body}>{truncate(e.body, 80)}</td>
                <td className="mono">{e.recallCount}</td>
                <td className="tt-tert">{formatDateTime(e.occurredAt)}</td>
                <td className="tt-tert">{formatDateTime(e.expiresAt)}</td>
                <td>
                  {e.disabledAt ? (
                    <span className="badge b-neutral">disabled</span>
                  ) : (
                    <span className="badge b-info">active</span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setConfirm({ mode: 'disable', event: e })}
                  >
                    Disable
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => setConfirm({ mode: 'delete', event: e })}
                    style={{ marginLeft: 8 }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirm?.mode === 'disable' ? (
        <MutationModal
          open
          title="Desactivar episodic event"
          confirmLabel="Desactivar"
          tone="warn"
          requiresReason
          isPending={disableMu.isPending}
          errorMessage={disableMu.error?.message ?? null}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => disableMu.mutate({ id: confirm.event.id, reason })}
        />
      ) : null}
      {confirm?.mode === 'delete' ? (
        <MutationModal
          open
          title="Eliminar episodic event"
          confirmLabel="Eliminar"
          tone="danger"
          requiresReason
          requiresLiteralConfirm="DELETE"
          isPending={deleteMu.isPending}
          errorMessage={deleteMu.error?.message ?? null}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => deleteMu.mutate({ id: confirm.event.id, reason })}
        />
      ) : null}
    </>
  );
}

// ─── Consolidations ───────────────────────────────────────────────────────

function ConsolidationsTab({ userId }: { userId: string }): ReactElement {
  const qc = useQueryClient();
  // El job es async (pg-boss): puede tardar 1–3s entre el send() y el INSERT
  // en memory_consolidations. Mientras esperamos, hacemos polling cada 1.5s.
  // Cuando aparece una row nueva (id distinto al top previo) o se cumplen 15s,
  // paramos.
  const [pendingJob, setPendingJob] = useState<{
    jobId: string;
    startedAt: number;
    prevTopId: string | null;
  } | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'user-memory-consolidations', userId],
    queryFn: () => fetchUserMemoryConsolidations(userId, { limit: 20 }),
    refetchInterval: pendingJob ? 1500 : false,
  });

  // Cortar el polling cuando aparece la row nueva o se vence el timeout (15s).
  const lastSettledRef = useRef<{ at: number; id: string } | null>(null);
  useEffect(() => {
    if (!pendingJob) return;
    const top = query.data?.items[0];
    const newRowVisible =
      top && top.id !== pendingJob.prevTopId && new Date(top.startedAt).getTime() >= pendingJob.startedAt - 1_000;
    if (newRowVisible && top) {
      lastSettledRef.current = { at: Date.now(), id: top.id };
      setPendingJob(null);
      return;
    }
    if (Date.now() - pendingJob.startedAt > 15_000) {
      setPendingJob(null);
    }
  }, [query.data, pendingJob]);

  const enqueueMu = useMutation({
    mutationFn: () => enqueueMemoryConsolidate(userId),
    onSuccess: (data) => {
      const top = query.data?.items[0];
      setPendingJob({
        jobId: data.jobId,
        startedAt: Date.now(),
        prevTopId: top?.id ?? null,
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-memory-consolidations', userId] });
    },
  });

  const justSettled =
    lastSettledRef.current && Date.now() - lastSettledRef.current.at < 4_000
      ? lastSettledRef.current.id
      : null;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--s-4)' }}>
        <button
          type="button"
          className="btn-primary"
          onClick={() => enqueueMu.mutate()}
          disabled={enqueueMu.isPending || Boolean(pendingJob)}
          data-testid="consolidate-now-btn"
        >
          {enqueueMu.isPending
            ? 'Encolando…'
            : pendingJob
              ? 'Esperando worker…'
              : 'Consolidate now'}
        </button>
      </div>
      {pendingJob ? (
        <div
          className="loading"
          role="status"
          data-testid="consolidate-pending"
          style={{ marginTop: 'var(--s-3)' }}
        >
          Job <span className="mono">{pendingJob.jobId}</span> encolado. Esperando que el
          worker termine (puede tardar unos segundos)…
        </div>
      ) : null}
      {enqueueMu.error ? (
        <div className="loading" role="alert">
          Error: {enqueueMu.error.message}
        </div>
      ) : null}
      {query.isPending ? (
        <div className="loading">Cargando consolidations…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : query.data.items.length === 0 ? (
        <div className="loading">
          No hay consolidations registradas aún. La memoria se consolida automáticamente
          cada noche a las 03:00 UTC, o disparalas manualmente con "Consolidate now".
        </div>
      ) : (
        <ul className="timeline" style={{ marginTop: 'var(--s-4)' }}>
          {query.data.items.map((r) => {
            const highlight = justSettled === r.id;
            return (
              <li
                key={r.id}
                style={{
                  padding: 'var(--s-3)',
                  borderBottom: '1px solid var(--c-border)',
                  background: highlight ? 'var(--c-warn-bg, rgba(255, 200, 100, 0.15))' : undefined,
                  transition: 'background 600ms ease-out',
                }}
              >
                <div style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'center' }}>
                  <span className="mono tt-tert">{new Date(r.startedAt).toISOString()}</span>
                  <span className={`badge ${badgeForTrigger(r.trigger)}`}>{r.trigger}</span>
                  <span className="tt-tert">{r.durationMs} ms</span>
                  {highlight ? (
                    <span className="badge b-info" data-testid="consolidate-just-finished">
                      recién finalizó
                    </span>
                  ) : null}
                </div>
                <div style={{ marginTop: 4 }}>
                  Expiró {r.expiredCount} episodic events, promovió {r.promotedCount}, evaluó{' '}
                  {r.promotionCandidates} candidatos.
                </div>
                {r.errorMessage ? (
                  <div style={{ marginTop: 4, color: 'var(--c-danger)' }}>
                    Error: {r.errorMessage}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function badgeForTrigger(t: 'cron' | 'admin' | 'bootstrap'): string {
  if (t === 'admin') return 'b-warn';
  if (t === 'bootstrap') return 'b-neutral';
  return 'b-info';
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}
